import { useEffect, useRef, useState } from "react";
import * as D from "@radix-ui/react-dialog";
import { ArrowUpRight, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";

const SIGNED = "fm_meditation_signed_up";
const DISMISSED = "fm_meditation_dismissed_until";
const DAYS_30 = 30 * 24 * 60 * 60 * 1000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NOTE = "Signing up also subscribes you to the Feeling Moved newsletter, where you'll receive little notes from Jake from time to time. You can unsubscribe anytime.";
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};
const hidden = () => store.get(SIGNED) === "1" || Number(store.get(DISMISSED) || 0) > Date.now();
const isPreview = () => new URLSearchParams(window.location.search).get("meditation-preview") === "1";

const Success = ({ res }) => (
  <div role="status" className="space-y-3" data-testid="meditation-success">
    <p className="font-display text-xl leading-snug text-ink">{res.message}</p>
    <a href={res.meditation_url} target="_blank" rel="noopener noreferrer" data-testid="meditation-success-link"
      className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ocean-deep px-6 text-sm font-semibold text-ivory transition-colors hover:bg-ink">
      Start the Meditation <ArrowUpRight className="h-4 w-4" />
    </a>
  </div>
);

const Form = ({ cfg, onDone }) => {
  const started = useRef(Date.now());
  const [f, setF] = useState({ email: "", consent_box: false, hp: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }); setError(""); };
  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(f.email.trim())) return setError("Please enter a valid email address.");
    if (cfg.requires_box && !f.consent_box) return setError("Please tick the box to receive the meditation.");
    setBusy(true);
    try {
      const r = await api.post("/meditation/signup", { ...f, email: f.email.trim(), source: "/ (pop-up)", elapsed_ms: Date.now() - started.current });
      onDone(r.data);
    } catch (err) {
      setError(err.response?.status === 422 ? err.response.data.detail : err.response?.status === 429 ? errMsg(err) : "Something went wrong, please try again.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-1.5 sm:space-y-2" data-testid="meditation-form">
      <label htmlFor="meditation-email" className="sr-only">Email address</label>
      <input id="meditation-email" type="email" inputMode="email" autoComplete="email" placeholder="Enter your email" value={f.email} onChange={set("email")} aria-invalid={!!error}
        className={`h-10 w-full rounded-full border bg-white/90 px-5 text-base text-ink placeholder:text-ink/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 sm:h-11 ${error ? "border-coral-ink" : "border-ink/15"}`} data-testid="meditation-email-input" />
      <div aria-hidden className="absolute left-[-10000px] h-px w-px overflow-hidden"><input tabIndex={-1} autoComplete="off" value={f.hp} onChange={set("hp")} data-testid="meditation-honeypot" /></div>
      {cfg.requires_box && (
        <label className="flex cursor-pointer items-start gap-2.5 text-[12px] leading-snug text-ink sm:text-[13px]" data-testid="meditation-consent-label">
          <input type="checkbox" checked={f.consent_box} onChange={set("consent_box")} className="mt-0.5 h-5 w-5 shrink-0 accent-[#1F4E5F]" data-testid="meditation-consent-checkbox" />
          <span>{cfg.box_text}</span>
        </label>
      )}
      {error && <p role="alert" className="text-[13px] font-medium text-coral-ink" data-testid="meditation-error">{error}</p>}
      <button type="submit" disabled={busy} className="h-10 w-full rounded-full bg-coral-ink px-6 text-sm font-semibold tracking-wide text-ivory transition-[background-color,transform] duration-300 hover:bg-[#8f3f22] active:scale-[0.98] disabled:opacity-70 sm:h-11" data-testid="meditation-submit-button">
        {busy ? "Sending…" : "Send Me the Meditation"}
      </button>
      <p className="text-[11px] leading-snug text-ink-soft sm:text-[11.5px]" data-testid="meditation-note">{NOTE}</p>
    </form>
  );
};

export const MeditationPopup = () => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({ requires_box: false, box_text: "" });
  const [res, setRes] = useState(null);
  const preview = useRef(isPreview());
  useEffect(() => {
    let t;
    api.get("/meditation/config").then((r) => {
      setCfg(r.data);
      if (preview.current) t = setTimeout(() => setOpen(true), 400);
      else if (r.data.enabled && !hidden()) t = setTimeout(() => !hidden() && setOpen(true), 9000);
    }).catch(() => {});
    return () => clearTimeout(t);
  }, []);
  const change = (v) => {
    setOpen(v);
    if (!v && !preview.current && store.get(SIGNED) !== "1") store.set(DISMISSED, String(Date.now() + DAYS_30));
  };
  const done = (r) => { if (!preview.current) store.set(SIGNED, "1"); setRes(r); };
  return (
    <D.Root open={open} onOpenChange={change}>
      <D.Portal>
        <D.Overlay data-testid="meditation-backdrop" className="fixed inset-0 z-[80] bg-[rgba(28,24,20,0.45)] backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <D.Content aria-describedby="meditation-lead" data-testid="meditation-dialog"
          onOpenAutoFocus={(e) => { e.preventDefault(); e.currentTarget.querySelector('[data-testid="meditation-close-button"]')?.focus(); }}
          className="fixed inset-x-0 bottom-0 z-[90] max-h-[50svh] overflow-y-auto rounded-t-[1.5rem] bg-ivory px-5 pb-4 pt-4 shadow-[0_-24px_60px_-36px_rgba(28,24,20,0.6)] focus:outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-8 data-[state=open]:fade-in-0 motion-reduce:animate-none sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90svh] sm:w-[min(420px,calc(100vw-32px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[1.75rem] sm:p-7">
          {preview.current && <p className="mb-2 inline-block rounded-full bg-sand px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink-soft" data-testid="meditation-preview-badge">Preview</p>}
          <D.Title className="pr-10 font-display text-[1.4rem] font-light leading-tight text-ink sm:text-[1.9rem]" data-testid="meditation-title">A Moment to Breathe</D.Title>
          <p id="meditation-lead" className="mt-0.5 font-display text-[15px] italic leading-snug text-ocean-deep sm:mt-1 sm:text-lg" data-testid="meditation-lead">Five minutes to pause, breathe and reconnect.</p>
          <div className="mt-1.5 space-y-1 text-[12.5px] leading-snug text-ink-soft sm:mt-2 sm:space-y-1.5 sm:text-sm" data-testid="meditation-body">
            <p>Sometimes all we need is a few minutes to create a little more space in our day.</p>
            <p>Enjoy a free five-minute guided meditation to help ease the mind, settle the body and return to a place of calm.</p>
            <p>A simple practice you can return to anytime.</p>
          </div>
          <div className="mt-2.5 sm:mt-3">{res ? <Success res={res} /> : <Form cfg={cfg} onDone={done} />}</div>
          <D.Close aria-label="Close" data-testid="meditation-close-button" className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/60">
            <X aria-hidden className="h-5 w-5" strokeWidth={1.8} />
          </D.Close>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
};
